import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'

interface MetricCardProps {
  title: string
  subtitle?: string
  value: number | string
  previousValue?: number
  prefix?: string
  suffix?: string
  loading?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

function formatValue(value: number, prefix?: string, suffix?: string): string {
  const formatted = typeof value === 'number' 
    ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
    : value
  return `${prefix || ''}${formatted}${suffix || ''}`
}

function calculateChange(current: number, previous: number): number {
  if (previous === 0) return 0
  return ((current - previous) / previous) * 100
}

export function MetricCard({
  title,
  subtitle,
  value,
  previousValue,
  prefix,
  suffix,
  loading,
  icon: Icon,
}: MetricCardProps) {
  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0
  const change = previousValue !== undefined ? calculateChange(numValue, previousValue) : undefined

  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 animate-pulse">
        <div className="h-4 bg-tf-bg-hover rounded w-20 mb-2" />
        <div className="h-7 bg-tf-bg-hover rounded w-28" />
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-sm text-tf-text-muted">{title}</p>
          {subtitle && (
            <p className="text-xs text-tf-text-muted">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className="w-6 h-6 rounded bg-tf-bg-muted flex items-center justify-center">
            <Icon className="w-3.5 h-3.5 text-tf-text-muted" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <p className="text-xl font-bold text-tf-text">
          {formatValue(numValue, prefix, suffix)}
        </p>
        {change !== undefined && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium',
              change >= 0
                ? 'bg-tf-feedback-success-bg text-green-700'
                : 'bg-tf-feedback-error-bg text-red-700'
            )}
          >
            {change >= 0 ? (
              <Icons.TrendingUp className="w-3 h-3" />
            ) : (
              <Icons.TrendingDown className="w-3 h-3" />
            )}
            {Math.abs(change).toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  )
}

export default MetricCard
